import { useState } from 'react'
import type { ReactNode } from 'react'
import type {
  DiscoverySummary,
  DraftState,
  PlannedOrderSettings,
  PrimerDiscovery,
  SettingChange,
  SettingSource
} from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import {
  describeSampleConfiguration,
  getSampleConfigurationLabel,
  getSequencingPlatformLabel,
  isProcessingTypeAllowedForPlatform,
  isSampleConfigurationAllowedForPlatform
} from '../../../Util/sequencingPlatform.ts'
import type { SampleConfigurationTypeKey } from '../../../Generated/SampleConfigurationType.ts'
import { SampleConfigurationTypes } from '../../../Generated/SampleConfigurationType.ts'
import { getProcessingTypeLabel, isProcessingTypeTaxaCombinationValid } from '../../../Util/processingType.ts'
import { ProcessingTypes } from '../../../Generated/ProcessingType.ts'
import type { ProcessingTypeKey } from '../../../Generated/ProcessingType.ts'
import type { TargetTaxonomyKey } from '../../../Generated/TargetTaxonomy.ts'
import type { TargetRegionKey } from '../../../Generated/TargetRegion.ts'
import { TargetRegions } from '../../../Generated/TargetRegion.ts'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import { ORDER_SETTINGS_TARGET } from '../../../Services/OrderWizard/issues.ts'
import { taxaOptions } from '../HomePage/01-CreateOrderPage/TaxaOptions.tsx'
import { taxonLabel } from './wizardLabels.ts'
import type { SequencingPlatformKey } from '../../../Generated/SequencingPlatform.ts'
import { plural } from '../../../Util/plural.ts'
import { RETRY_BUTTON } from './retryStyle.ts'
import { InlineWarning, SectionHeading } from './Primitives.tsx'
import type { GivenAnswer, ReopenableKind } from '../../../Services/OrderWizard/answers.ts'
import { layoutSwitchOf, regionOf, trimSwitchOf } from '../../../Services/OrderWizard/orderSettingsPlan.ts'
import { joinPreviewText, reverseJoinPreview } from '../../../Services/OrderWizard/reverseFiles.ts'
import { countItems } from '../../../Services/OrderWizard/sampleLabels.ts'
import { placedGroups } from '../../../Services/OrderWizard/pools.ts'
import type { PairLabel } from '../../../Services/OrderWizard/discovery.ts'
import { pairLabel } from '../../../Services/OrderWizard/discovery.ts'
import { writtenEstimate } from '../../../Services/OrderWizard/primerOrientation.ts'

/* the reasoning behind a confident value is a tooltip; only an uncertain one spells it out */
const Row = ({ label, value, note, warn }: { label: string; value: ReactNode; note?: string; warn?: string }) => (
  <div className="grid grid-cols-1 gap-x-3 border-b border-slate-100 py-1.5 last:border-b-0 sm:grid-cols-[11rem_1fr]">
    <dt className="text-sm text-slate-500">{label}</dt>
    <dd className="text-sm text-slate-900" title={warn ? undefined : note}>
      <div className="flex flex-wrap items-center gap-2">
        {value}
        {warn && (
          <InlineWarning layout="badge" data-testid="wizard-low-confidence">
            {warn}
          </InlineWarning>
        )}
      </div>
      {warn && note && <p className="mt-0.5 text-xs text-slate-500">{note}</p>}
    </dd>
  </div>
)

const PAIR_BASIS_TEXT: Record<NonNullable<PairLabel['basis']>, string> = {
  'earlier-orders': 'as on your earlier orders',
  reads: 'as the reads show'
}
const COMPATIBLE_SHOWN = 3

const primerValue = (primers: PrimerDiscovery, draft?: DraftState): ReactNode => {
  switch (primers.status) {
    case 'found': {
      const label = pairLabel(primers.pair, draft ? placedGroups(draft).map((group) => writtenEstimate(draft, group)) : [])
      const shown = label.compatible.slice(0, COMPATIBLE_SHOWN)
      const more = label.compatible.length - shown.length
      return (
        <span>
          <span className="font-medium">{label.name ?? 'Registered pair'}</span>
          {label.basis && <span className="text-slate-500"> ({PAIR_BASIS_TEXT[label.basis]})</span>}
          <span className="text-slate-500"> · amplicon {primers.pair.amplicons.map(taxonLabel).join(', ')}</span>
          {shown.length > 0 && (
            <span className="block text-xs text-slate-500" title={label.compatible.join('; ')} data-testid="wizard-pair-compatible">
              compatible with: {shown.join('; ')}
              {more > 0 && ` (+${more} more)`}
            </span>
          )}
        </span>
      )
    }
    case 'mixed':
      return <span>Several pairs: {primers.pairs.map((pair) => pair.names[0] ?? pair.amplicons.join('/')).join('; ')}</span>
    case 'partial': {
      const amplicons = [...new Set(primers.candidates.flatMap((pair) => pair.amplicons))]
      const shown = primers.primerNames.slice(0, 3)
      const more = primers.primerNames.length - shown.length
      return (
        <span>
          <span className="capitalize">{primers.side}</span>:{' '}
          <span className="font-medium">{shown.join(' / ') || 'registered primer'}</span>
          {more > 0 && <span className="text-slate-500"> (+{more} more)</span>}
          <span className="text-slate-500">
            {' '}
            · {primers.side === 'forward' ? 'reverse' : 'forward'} not identified · amplicon {amplicons.map(taxonLabel).join(', ')}
          </span>
        </span>
      )
    }
    case 'none':
      return <span>No registered pair found</span>
    case 'registry-unavailable':
      return <span>Not checked</span>
  }
}

const SOURCE_TEXT: Record<SettingSource, string> = {
  detected: 'detected',
  default: 'your default',
  fallback: 'fallback',
  answered: 'your answer',
  entered: 'entered',
  undetermined: 'not detected'
}

const UNDETERMINED_TEXT = 'Not detected: answer the question above'

type Option = { value: string; label: string; disabled?: string }

/** A setting's value with a quiet "Change" that swaps it for a select in place. */
const Changeable = ({
  label,
  text,
  value,
  options,
  hint,
  onChange
}: {
  label: string
  text: string
  value: string
  options: Option[]
  /** Shown under the open select: what choosing would do. */
  hint?: ReactNode
  onChange: (value: string) => void
}) => {
  const [open, setOpen] = useState(false)
  if (!open) {
    return (
      <>
        <span>{text}</span>
        <button
          type="button"
          className="text-xs text-slate-500 hover:text-slate-800 hover:underline"
          data-testid={`wizard-setting-change-${label}`}
          aria-label={`Change the ${label}`}
          onClick={() => setOpen(true)}
        >
          Change
        </button>
      </>
    )
  }
  const select = (
    <select
      autoFocus
      className="select select-bordered select-xs"
      aria-label={label}
      data-testid={`wizard-setting-select-${label}`}
      value={value}
      onBlur={() => setOpen(false)}
      onKeyDown={(event) => event.key === 'Escape' && setOpen(false)}
      onChange={(event) => {
        setOpen(false)
        if (event.target.value !== value) {
          onChange(event.target.value)
        }
      }}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={!!option.disabled} title={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  )
  return hint ? (
    <span className="flex flex-col items-start gap-0.5">
      {select}
      <span className="text-xs text-slate-600" data-testid={`wizard-setting-hint-${label}`}>
        {hint}
      </span>
    </span>
  ) : (
    select
  )
}

const taxonOptions = (processingType: ProcessingTypeKey): Option[] =>
  taxaOptions.map((option) => ({
    value: option.value,
    label: option.label,
    disabled: isProcessingTypeTaxaCombinationValid(processingType, option.value)
      ? undefined
      : `${getProcessingTypeLabel(processingType)} is not available for this target`
  }))

const processingTypeOptions = (platform: SequencingPlatformKey | null, taxon: TargetTaxonomyKey): Option[] =>
  (Object.keys(ProcessingTypes) as ProcessingTypeKey[]).map((key) => ({
    value: key,
    label: getProcessingTypeLabel(key),
    disabled: !isProcessingTypeAllowedForPlatform(key, platform)
      ? `${getSequencingPlatformLabel(platform)} data cannot be processed this way`
      : !isProcessingTypeTaxaCombinationValid(key, taxon)
        ? `not available for ${taxonLabel(taxon)}`
        : undefined
  }))

const answerText = (answer: GivenAnswer): string => {
  switch (answer.kind) {
    case 'read-layout':
      return answer.answer === 'merged' ? 'merged reads' : `R${answer.mate === 1 ? 2 : 1} files to come`
    case 'primer-trimming':
      return answer.answer === 'on' ? 'primers on the reads' : 'primers removed'
    case 'format-multiplex':
      return answer.answer === 'single' ? 'one sample per file pair' : 'several samples per file pair'
    case 'target-taxon':
      return taxonLabel(answer.answer)
    case 'processing-type-conflict':
      return getProcessingTypeLabel(answer.answer)
  }
}

/** "Answered: merged reads — Change": the question comes back, as long as the order does not exist. */
const AnswerLines = ({
  answers,
  kinds,
  onReopen
}: {
  answers: readonly GivenAnswer[]
  kinds: readonly ReopenableKind[]
  onReopen: (kind: ReopenableKind) => void
}) => (
  <>
    {answers
      .filter((answer) => kinds.includes(answer.kind))
      .map((answer) => (
        <span key={answer.kind} className="block w-full text-xs text-slate-600" data-testid="wizard-answered">
          Answered: {answerText(answer)} —{' '}
          <button
            type="button"
            className="text-slate-500 hover:text-slate-800 hover:underline"
            data-testid={`wizard-answered-change-${answer.kind}`}
            onClick={() => onReopen(answer.kind)}
          >
            Change
          </button>
        </span>
      ))}
  </>
)

const FORMAT_ANSWERS: ReopenableKind[] = ['read-layout', 'primer-trimming', 'format-multiplex']

const impliesMerged = (value: SampleConfigurationTypeKey) => SampleConfigurationTypes[value].impliesMerged

/** The formats a created order can switch to: primers on/off and merged/paired, never to or from several samples per pair. */
const formatChoices = (value: SampleConfigurationTypeKey, platform: SequencingPlatformKey | null): SampleConfigurationTypeKey[] => {
  const layout = layoutSwitchOf(value)
  const choices = [value, trimSwitchOf(value), layout, layout && trimSwitchOf(layout)].filter(
    (choice): choice is SampleConfigurationTypeKey =>
      !!choice && (choice === value || isSampleConfigurationAllowedForPlatform(choice, platform))
  )
  return [...new Set(choices)]
}

/** What a layout switch would do to the samples, shown before it is chosen. */
const layoutHint = (draft: DraftState | undefined, value: SampleConfigurationTypeKey): ReactNode => {
  if (!draft) {
    return undefined
  }
  if (impliesMerged(value)) {
    const preview = joinPreviewText(reverseJoinPreview(draft))
    return preview ? `Paired-end: ${preview}.` : undefined
  }
  const paired = placedGroups(draft).filter((group) => !!group.reverse)
  return paired.length > 0
    ? `Merged reads: refused while ${countItems(paired)} ${paired.length === 1 ? 'holds' : 'hold'} an R2 file.`
    : undefined
}

/** After a refused switch to merged reads: the samples holding an R2 go, "Re-add" brings their files back one sample each. */
const PairedSamplesBlock = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  const format = draft.settingsWrite?.sampleConfiguration
  const paired = placedGroups(draft).filter((group) => !!group.reverse)
  if (draft.settingsWrite?.status !== 'failed' || !format || !impliesMerged(format) || paired.length === 0) {
    return null
  }
  return (
    <p className="mb-1 text-sm text-slate-700" data-testid="wizard-paired-block">
      <button
        type="button"
        className={`${RETRY_BUTTON} px-0`}
        data-testid="wizard-remove-paired"
        onClick={() => {
          dispatch(wizard.itemsRemoved({ draftId: draft.id, groupKeys: paired.map((group) => group.key) }))
          dispatch(wizard.settingsRetryRequested({ draftId: draft.id }))
        }}
      >
        Remove the {countItems(paired)} with an R2 file and switch
      </button>{' '}
      — “Re-add” then brings each of their files back as a sample of merged reads.
    </p>
  )
}

/**
 * The order's settings; with `edit`, they can be changed, which goes into the order: target, region,
 * processing type, singletons, and the sample format between primers on/off and merged/paired.
 */
export const OrderSettingsSection = ({
  settings,
  platform,
  edit,
  draft,
  answers = [],
  onReopen = () => undefined
}: {
  settings: PlannedOrderSettings
  platform: SequencingPlatformKey | null
  edit?: Pick<DraftState, 'id' | 'settingsWrite' | 'changedInClassic'>
  /** The whole draft, for what a merged/paired switch would do to its samples. */
  draft?: DraftState
  /** Answers given before the order exists, each with its "Change". */
  answers?: readonly GivenAnswer[]
  onReopen?: (kind: ReopenableKind) => void
}) => {
  const inClassic = (setting: SettingChange['setting'], warn?: string) =>
    edit?.changedInClassic?.includes(setting) ? 'changed in the classic view' : warn
  const dispatch = useDispatch()
  const change = (next: SettingChange) => edit && dispatch(wizard.settingChanged({ draftId: edit.id, change: next }))
  const write = edit?.settingsWrite
  const taxonText =
    settings.targetTaxonomy.source === 'undetermined'
      ? UNDETERMINED_TEXT
      : `${taxonLabel(settings.targetTaxonomy.value)} (${SOURCE_TEXT[settings.targetTaxonomy.source]})`
  const processingText = `${getProcessingTypeLabel(settings.processingType.value)} (${SOURCE_TEXT[settings.processingType.source]})`
  const formatText =
    settings.sampleConfiguration.source === 'undetermined'
      ? UNDETERMINED_TEXT
      : `${getSampleConfigurationLabel(settings.sampleConfiguration.value, platform)} (${SOURCE_TEXT[settings.sampleConfiguration.source]})`
  const region = regionOf(settings.targetTaxonomy, settings.targetRegion)
  const regionText = region && `${TargetRegions[region].label} (${settings.targetRegionEntered ? 'entered' : 'default'})`
  /* the order update re-flags the items; it refuses merged reads while a sample holds an R2 */
  const formats = formatChoices(settings.sampleConfiguration.value, platform)
  const current = settings.sampleConfiguration.value
  return (
    <section id={ORDER_SETTINGS_TARGET} className="transition-shadow" data-testid="wizard-order-settings">
      <SectionHeading className="mb-1 flex flex-wrap items-baseline gap-x-2">
        Order settings
        {write?.status === 'pending' && (
          <span className="text-xs font-normal normal-case tracking-normal text-slate-500" data-testid="wizard-settings-saving">
            Saving…
          </span>
        )}
      </SectionHeading>
      {write?.status === 'failed' && (
        <p className="mb-1 flex flex-wrap items-center gap-x-2 text-sm text-error" data-testid="wizard-settings-failed">
          <span>Not saved{write.error ? `: ${write.error}` : ''}</span>
          <button
            type="button"
            className={`${RETRY_BUTTON} px-0`}
            onClick={() => edit && dispatch(wizard.settingsRetryRequested({ draftId: edit.id }))}
          >
            Try again
          </button>
        </p>
      )}
      {draft && <PairedSamplesBlock draft={draft} />}
      <dl>
        <Row
          label="Sample format"
          value={
            <>
              {edit && formats.length > 1 ? (
                <Changeable
                  label="sample format"
                  text={formatText}
                  value={current}
                  options={formats.map((value) => ({
                    value,
                    label: getSampleConfigurationLabel(value, platform)
                  }))}
                  hint={
                    <>
                      {describeSampleConfiguration(current, platform)}
                      {layoutSwitchOf(current) && formats.includes(layoutSwitchOf(current)!) && <> {layoutHint(draft, current)}</>}
                    </>
                  }
                  onChange={(value) => change({ setting: 'sampleConfiguration', value: value as SampleConfigurationTypeKey })}
                />
              ) : (
                formatText
              )}
              <AnswerLines answers={answers} kinds={FORMAT_ANSWERS} onReopen={onReopen} />
            </>
          }
          note={settings.sampleConfiguration.source === 'detected' ? undefined : settings.sampleConfiguration.reason}
          warn={inClassic('sampleConfiguration')}
        />
        <Row
          label="Target taxon"
          value={
            edit ? (
              <Changeable
                label="target taxon"
                text={taxonText}
                value={settings.targetTaxonomy.value}
                options={taxonOptions(settings.processingType.value)}
                onChange={(value) => change({ setting: 'targetTaxonomy', value: value as TargetTaxonomyKey })}
              />
            ) : (
              <>
                {taxonText}
                <AnswerLines answers={answers} kinds={['target-taxon']} onReopen={onReopen} />
              </>
            )
          }
          note={settings.targetTaxonomy.reason}
          warn={inClassic('targetTaxonomy', settings.targetTaxonomy.source === 'fallback' ? 'please check' : undefined)}
        />
        <Row
          label="Processing type"
          value={
            edit ? (
              <Changeable
                label="processing type"
                text={processingText}
                value={settings.processingType.value}
                options={processingTypeOptions(platform, settings.targetTaxonomy.value)}
                onChange={(value) => change({ setting: 'processingType', value: value as ProcessingTypeKey })}
              />
            ) : (
              <>
                {processingText}
                <AnswerLines answers={answers} kinds={['processing-type-conflict']} onReopen={onReopen} />
              </>
            )
          }
          note={settings.processingType.source === 'fallback' ? settings.processingType.reason : undefined}
          warn={inClassic('processingType')}
        />
        {regionText && (
          <Row
            label="Target region"
            value={
              edit ? (
                <Changeable
                  label="target region"
                  text={regionText}
                  value={region}
                  options={Object.values(TargetRegions).map(({ key, label }) => ({ value: key, label }))}
                  onChange={(value) => change({ setting: 'targetRegion', value: value as TargetRegionKey })}
                />
              ) : (
                regionText
              )
            }
            note="The BOLD reference database CO1 - Animal reads are matched against."
            warn={inClassic('targetRegion')}
          />
        )}
        {edit && (
          <Row
            label="Singletons"
            value={
              <label className="inline-flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  className="toggle toggle-xs"
                  data-testid="wizard-setting-singletons"
                  aria-label="Keep singletons"
                  checked={settings.allowSingletons}
                  onChange={(event) => change({ setting: 'allowSingletons', value: event.target.checked })}
                />
                <span>
                  {settings.allowSingletons ? 'kept' : 'removed'}
                  {settings.allowSingletonsSource !== 'entered' && <span className="text-slate-500"> (default)</span>}
                </span>
              </label>
            }
            note="Sequences seen only once in the whole order: removed as likely errors, or kept."
            warn={inClassic('allowSingletons')}
          />
        )}
      </dl>
    </section>
  )
}

export const DiscoverySummaryView = ({
  discovery,
  settings,
  edit,
  draft,
  answers,
  onReopen
}: {
  discovery: DiscoverySummary
  settings?: PlannedOrderSettings
  /** The draft whose settings may be changed here; without it they are shown only. */
  edit?: Pick<DraftState, 'id' | 'settingsWrite' | 'changedInClassic'>
  draft?: DraftState
  answers?: readonly GivenAnswer[]
  onReopen?: (kind: ReopenableKind) => void
}) => {
  const platform: SequencingPlatformKey | null = discovery.platform === 'UNKNOWN' ? null : discovery.platform
  const classifier = discovery.classifier
  const lowFormat = classifier.configuration === 'UNKNOWN' || classifier.confidence === 'low'
  /* the reads left the layout open and the lab's answer settled it: the files card no longer calls it unclear */
  const layoutSettled = discovery.layout.value === 'UNKNOWN' && !!settings && settings.sampleConfiguration.source !== 'undetermined'
  return (
    <div className="space-y-4" data-testid="wizard-discovery">
      <section>
        <SectionHeading className="mb-1">Detected from {plural(discovery.filesScanned, 'file')}</SectionHeading>
        <dl>
          <Row label="Platform" value={getSequencingPlatformLabel(platform) ?? 'Unknown'} warn={platform ? undefined : 'not detected'} />
          {discovery.runs && discovery.runs.count > 0 && (
            <Row
              label="Sequencing runs"
              value={`${plural(discovery.runs.count, 'run')} in the scanned files`}
              note="Information only: pools are not derived from runs."
            />
          )}
          <Row
            label="Read layout"
            value={
              discovery.layout.value === 'PAIRED'
                ? 'Paired-end (R1 + R2)'
                : discovery.layout.value === 'MERGED_OR_SINGLE'
                  ? 'One file per sample'
                  : layoutSettled
                    ? `${settings && impliesMerged(settings.sampleConfiguration.value) ? 'One file per sample' : 'Paired-end (R1 + R2)'}, as the sample format says`
                    : 'Unknown'
            }
            note={discovery.layout.reason}
            warn={
              discovery.layout.confidence === 'high' || layoutSettled
                ? undefined
                : discovery.layout.confidence === 'names-only'
                  ? 'from file names only'
                  : 'unclear'
            }
          />
          <Row
            label="Primer pair"
            value={primerValue(discovery.primers, draft)}
            note={discovery.primers.reason}
            warn={
              discovery.primers.status === 'found'
                ? undefined
                : discovery.primers.status === 'partial'
                  ? 'pair not identified'
                  : 'no primers identified'
            }
          />
          <Row
            label="Primers on reads"
            value={
              discovery.trimming === 'TRIMMED'
                ? 'Removed (trimmed)'
                : discovery.trimming === 'UNTRIMMED'
                  ? 'Present (untrimmed)'
                  : 'Unclear'
            }
            warn={discovery.trimming === 'UNKNOWN' ? 'unclear' : undefined}
          />
          {!settings && (
            <Row
              label="Sample format"
              value={
                <span>
                  {classifier.configuration === 'UNKNOWN'
                    ? 'Not determined'
                    : getSampleConfigurationLabel(classifier.configuration, platform)}
                  {classifier.confidence && <span className="text-slate-500"> · {classifier.confidence} confidence</span>}
                </span>
              }
              note={classifier.reason}
              warn={lowFormat ? (classifier.configuration === 'UNKNOWN' ? 'not determined' : 'low confidence') : undefined}
            />
          )}
        </dl>
      </section>
      {settings && (
        <OrderSettingsSection settings={settings} platform={platform} edit={edit} draft={draft} answers={answers} onReopen={onReopen} />
      )}
    </div>
  )
}
